import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { AppLayout } from '@/layouts/AppLayout/AppLayout.tsx';
import { logger } from '@/logger.ts';
import { RouteSync } from '@/routing/RouteSync.tsx';
import { OnboardingWizard } from '@/spaces/onboarding';
import { readDevOnboardingStep } from '@/spaces/onboarding/devOnboardingStep';
import { OnboardingTransitionProvider } from '@/spaces/onboarding/onboardingTransition';
import type { SlotId } from '@/spaces/onboarding/screens/strategySummary';
import { Spaces } from '@/spaces/Spaces.tsx';
import { trackEvent } from '@/utils/fullstory';

function App() {
	const bridgeApi = useBridgeApi();
	const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
	const [pillarsOverride, setPillarsOverride] = useState<SlotId[] | null>(null);

	const phase = bridgeApi.user_state?.onboarding_status?.phase;
	// A dev-only `?onboardingStep=` deep-link force-mounts the wizard even for an
	// already-onboarded user, so a specific step can be opened for visual QA +
	// e2e. Latched once per load; inert in production builds.
	const [devOnboardingStep] = useState(() => readDevOnboardingStep());
	const isDone =
		!devOnboardingStep && (phase === 'done' || isOnboardingComplete);

	const completeOnboarding = useCallback(
		(pillars: SlotId[]) => {
			const reduceMotion = window.matchMedia(
				'(prefers-reduced-motion: reduce)',
			).matches;
			const supportsViewTransition =
				typeof document.startViewTransition === 'function';

			const swapUi = () => {
				flushSync(() => {
					setPillarsOverride(pillars);
					setIsOnboardingComplete(true);
				});
			};
			const finalize = () => {
				bridgeApi.sendAction({
					type: 'do_set_onboarding_step',
					phase: 'done',
					pillars,
				});
				trackEvent('Onboarding Completed');
			};

			if (supportsViewTransition && !reduceMotion) {
				try {
					const transition = document.startViewTransition(swapUi);
					// then(finalize, finalize), not finally: finished rejects when the
					// morph is skipped/aborted; finalize must fire on both outcomes and
					// the rejection must be consumed so it isn't left unhandled.
					transition.finished.then(finalize, finalize);
				} catch (error) {
					logger.error('Onboarding view transition failed', { error });
					swapUi();
					finalize();
				}
			} else {
				swapUi();
				finalize();
			}
		},
		[bridgeApi],
	);

	return (
		<OnboardingTransitionProvider
			completeOnboarding={completeOnboarding}
			pillarsOverride={pillarsOverride}
		>
			{!isDone ? (
				<OnboardingWizard />
			) : (
				<>
					<RouteSync />
					<AppLayout showNavBar>
						<Spaces />
					</AppLayout>
				</>
			)}
		</OnboardingTransitionProvider>
	);
}

export { App };
